// Custom apparel (DTF) pricing, implementing section 2 of the NMS
// "Web Developer Implementation Guide" in its stated order of operations:
//   1. raw base price (unrounded)
//   2. quantity tier multiplier, then round UP to a whole dollar
//   3. flat per-garment add-ons (size, personalization, bagging)
//   4. order fees, then the rush multiplier, then delivery
// Every number comes from the config object, never from this file.

import { roundUpDollar, cents } from "./money.js";
import { resolveSize, validateRosterRow } from "./roster.js";

const findTier = (tiers, qty) =>
  tiers.find((t) => qty >= t.min && (t.max == null || qty <= t.max));

/** Total garment count on a line, from size quantities or roster rows. */
export function lineQuantity(line) {
  if (line.kind === "roster") {
    return (line.roster ?? []).reduce((n, r) => n + (Number(r.qty) > 0 ? Number(r.qty) : 0), 0);
  }
  return Object.values(line.sizes ?? {}).reduce((n, q) => n + (Number(q) > 0 ? Number(q) : 0), 0);
}

/** Garments per size id on a line. */
function sizeCounts(line, sizes) {
  const counts = {};
  if (line.kind === "roster") {
    for (const r of line.roster ?? []) {
      const id = resolveSize(r.size, sizes);
      if (id && Number(r.qty) > 0) counts[id] = (counts[id] ?? 0) + Number(r.qty);
    }
  } else {
    for (const [id, q] of Object.entries(line.sizes ?? {})) {
      if (Number(q) > 0) counts[id] = Number(q);
    }
  }
  return counts;
}

/**
 * Step 1: raw base price for one garment on this line.
 * Returns { amount, parts: [{label, amount}], errors }.
 */
export function rawBasePrice(line, config, mode = "public") {
  const k = config.constants;
  const parts = [];
  const errors = [];

  if (line.customerSupplied) {
    if (mode !== "internal") errors.push("Customer-supplied garments can only be quoted in staff mode.");
    parts.push({ label: "Customer-supplied blank", amount: 0 });
    parts.push({ label: "Customer garment handling fee", amount: k.customerGarmentHandlingFee });
  } else {
    let wholesale = null;
    if (line.customWholesale != null && line.customWholesale !== "") {
      if (mode !== "internal") errors.push("Custom wholesale costs can only be entered in staff mode.");
      wholesale = Number(line.customWholesale);
    } else {
      const product = config.catalog.products.find((p) => p.id === line.productId);
      if (!product) errors.push("Choose a garment.");
      else wholesale = product.wholesale;
    }
    if (wholesale != null) {
      if (!(wholesale >= 0)) errors.push("Wholesale cost must be a positive number.");
      else parts.push({ label: `Garment (${wholesale.toFixed(2)} x ${k.markupMultiplier} markup)`, amount: wholesale * k.markupMultiplier });
    }
  }

  const decals = line.decals ?? [];
  if (!decals.length) errors.push("Add at least one decal.");
  decals.forEach((d, i) => {
    const placement = config.placements.find((p) => p.id === d.placement);
    if (!placement) { errors.push(`Decal ${i + 1}: choose a placement.`); return; }
    if (placement.requiresNote && !String(d.note ?? "").trim()) {
      errors.push(`Decal ${i + 1}: describe the exact placement.`);
    }
    parts.push({
      label: i === 0 ? `First decal (${placement.label})` : `Additional decal (${placement.label})`,
      amount: i === 0 ? k.firstDecalFee : k.additionalDecalFee,
    });
    if (d.oversized) {
      if (!(Number(d.widthIn) > 0 && Number(d.heightIn) > 0)) {
        errors.push(`Decal ${i + 1}: enter the oversized width and height.`);
      }
      parts.push({ label: `Oversized surcharge (${placement.label})`, amount: k.oversizedDecalSurcharge });
    }
    if (placement.sleeve && config.rules.sleeveSurchargeStage === "base") {
      parts.push({ label: `Sleeve labor (${placement.label})`, amount: k.sleeveDecalSurcharge });
    }
  });

  const amount = parts.reduce((s, p) => s + p.amount, 0);
  return { amount, parts, errors };
}

/** Step 2: tier price for one garment, rounded up to whole dollars. */
export function tierUnitPrice(rawBase, qty, config) {
  const tier = findTier(config.quantityTiers, qty);
  if (!tier) return { tier: null, unit: null };
  const k = config.constants;
  const unit = tier.microRun
    ? Math.max(k.microRunFloor, roundUpDollar(rawBase + k.microRunPremium))
    : roundUpDollar(rawBase * tier.multiplier);
  return { tier, unit };
}

/**
 * Price a full apparel order.
 * @param {object} order  see README "Garment order shape"
 * @param {object} config parsed config/garments.json
 */
export function quoteGarments(order, config) {
  const k = config.constants;
  const mode = order.mode === "internal" ? "internal" : "public";
  const errors = [];
  const warnings = [];
  const lines = order.lines ?? [];
  if (!lines.length) errors.push("Add at least one garment.");

  const orderQty = lines.reduce((n, l) => n + lineQuantity(l), 0);

  const pricedLines = lines.map((line, idx) => {
    const name = line.label || `Item ${idx + 1}`;
    const qty = lineQuantity(line);
    const lineErrors = [];
    const product = config.catalog.products.find((p) => p.id === line.productId);

    let rowErrors = [];
    if (line.kind === "roster") {
      const colors = line.customerSupplied || line.customWholesale ? null : product?.colors;
      rowErrors = (line.roster ?? []).map((r) => validateRosterRow(r, { sizes: config.sizes, colors }));
      const bad = rowErrors.filter((e) => e.length).length;
      if (bad) lineErrors.push(`${bad} roster row${bad > 1 ? "s need" : " needs"} correcting.`);
    }
    if (qty < 1) lineErrors.push("Enter at least one garment.");

    const base = rawBasePrice(line, config, mode);
    lineErrors.push(...base.errors);

    const tierQty = config.rules.tierQuantityScope === "order" ? orderQty : qty;
    const { tier, unit } = qty > 0 ? tierUnitPrice(base.amount, tierQty, config) : { tier: null, unit: null };

    const roster = line.kind === "roster";
    const bagged = roster || !!order.bagging;
    const sleeveFlat =
      config.rules.sleeveSurchargeStage === "flat"
        ? (line.decals ?? []).filter((d) => config.placements.find((p) => p.id === d.placement)?.sleeve).length *
          k.sleeveDecalSurcharge
        : 0;

    const addons = [];
    if (sleeveFlat) addons.push({ label: "Sleeve labor", amount: sleeveFlat });
    if (roster) addons.push({ label: "Variable-data personalization", amount: k.personalizationFee });
    if (bagged) addons.push({ label: roster ? "Kitting & bagging (required for rosters)" : "White-glove kitting & bagging", amount: k.baggingFee });
    const addonPerGarment = addons.reduce((s, a) => s + a.amount, 0);

    const counts = sizeCounts(line, config.sizes);
    const sizeGroups = config.sizes
      .filter((s) => counts[s.id])
      .map((s) => {
        const each = unit == null ? null : cents(unit + s.upcharge + addonPerGarment);
        return { size: s.id, label: s.label, qty: counts[s.id], upcharge: s.upcharge, unit: each, total: each == null ? null : cents(each * counts[s.id]) };
      });
    const subtotal = unit == null ? 0 : cents(sizeGroups.reduce((s, g) => s + g.total, 0));

    lineErrors.forEach((e) => errors.push(`${name}: ${e}`));
    return {
      id: line.id ?? String(idx),
      label: name,
      kind: roster ? "roster" : "bulk",
      qty,
      tierQty,
      rawBase: cents(base.amount),
      rawBaseParts: base.parts.map((p) => ({ ...p, amount: cents(p.amount) })),
      tier: tier && { label: tier.label, multiplier: tier.multiplier, microRun: !!tier.microRun },
      tierUnit: unit,
      addons,
      sizeGroups,
      subtotal,
      rowErrors,
      errors: lineErrors,
    };
  });

  const itemsSubtotal = cents(pricedLines.reduce((s, l) => s + l.subtotal, 0));

  const orderFees = [];
  if (order.artworkPolicy === "proof") orderFees.push({ label: "Physical flight-check proof", amount: k.flightCheckProofFee });
  const preRush = cents(itemsSubtotal + orderFees.reduce((s, f) => s + f.amount, 0));

  const rush = config.rush.find((r) => r.id === (order.rush ?? "standard")) ?? config.rush[0];
  const afterRush = cents(preRush * rush.multiplier);

  let delivery = null;
  const method = config.delivery.methods.find((m) => m.id === order.delivery?.method);
  if (method) {
    const zip = String(order.delivery?.zip ?? "").trim().slice(0, 5);
    if (method.requiresLocalZip && !config.delivery.courierZips.includes(zip)) {
      errors.push("Local courier delivery is only available to Warren County ZIP codes.");
    }
    delivery = {
      id: method.id,
      label: method.label,
      amount: method.carrierCalculated ? null : method.price,
      note: method.carrierCalculated ? "Calculated by carrier at checkout" : method.minimum ? "Minimum; final fee may be higher" : null,
    };
  }

  const status = errors.length ? "incomplete" : order.artworkPolicy === "design" ? "quote-requested" : "priced";
  if (order.artworkPolicy === "design") {
    warnings.push(`Design support is billed at $${k.designSupportHourly}/hour. A designer will send a time estimate before work begins.`);
  }
  if (order.artworkPolicy === "proof") warnings.push("Physical proofs add 3-5 days to production.");
  if (lines.some((l) => l.customerSupplied)) {
    warnings.push("Customer-supplied garments: print the liability waiver and have the customer sign it before production.");
  }

  const total = cents(afterRush + (delivery?.amount ?? 0));
  let expiresOn = null;
  if (mode === "internal") {
    const d = new Date(order.quoteDate ?? Date.now());
    d.setDate(d.getDate() + k.quoteValidDays);
    expiresOn = d.toISOString().slice(0, 10);
  }

  return {
    category: config.id,
    mode,
    status,
    lines: pricedLines,
    itemsSubtotal,
    orderFees,
    preRushSubtotal: preRush,
    rush: { id: rush.id, label: rush.label, multiplier: rush.multiplier, amount: cents(afterRush - preRush) },
    delivery,
    total,
    totalExcludesShipping: delivery?.amount == null,
    garmentCount: orderQty,
    expiresOn,
    warnings,
    errors,
  };
}
